import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, TextField, Button, Chip, Tooltip, IconButton, CircularProgress, Card, CardContent } from '@mui/material';
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject, getMetadata } from 'firebase/storage';
import { storage } from '../firebase/firebase';
import * as Icons from '../constants/icons';
import { useConfirm } from '../providers/ConfirmProvider';
import { useNotification } from '../providers/NotificationProvider';
import { STANDARD_CTA_BUTTON_COMPACT_SX } from '../constants/buttonStyles';
import { EventMaster, EventInviteeRow } from '../hooks/useEventManagement';

/**
 * イベント詳細「案内メール」タブ。
 *
 * 一斉配信で使う案内メールの文面 (件名・本文は mst_event) と
 * 添付PDF (Firebase Storage event-mail/{event_code}/) をイベント単位で管理する。
 * 本文はプレースホルダー {{会社名}} {{氏名}} に対応し、配信予定の実データ1件で
 * 差し込み後のプレビューを表示する。編集は event.master.edit 保有者のみ。
 */

// 添付合計の上限 (SMTPリレー配信を考慮した目安)
const MAX_TOTAL_BYTES = 10 * 1024 * 1024;

type AttachmentItem = {
  name: string;
  fullPath: string;
  size: number;
  updated: string | null;
};

const formatBytes = (n: number): string => {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
};

// プレースホルダーを宛先1件の実データへ差し込む (配信スクリプトと同一ルール)
//   - 氏名なし (法人宛) は会社名へ「　御中」を付け、氏名と直後の「様」は行ごと除去
//   - 役職なし等で生じた連続スペースは1つに詰め、行頭・行末のスペースは除去
//   - 置換の結果テキストが空になった行 (会社なしの会社名行など) は行ごと削除
//   - {{申込URL}} は申込サイトURL + 宛先固有トークン (?listID=) に置換
const PLACEHOLDER_RE = /\{\{(会社名|役職|氏名|申込URL)\}\}/;
const buildEntryUrl = (entryUrl: string | null, token: string | null): string => {
  if (!entryUrl) return '';
  return token ? `${entryUrl}?listID=${token}` : entryUrl;
};
const applyPlaceholders = (text: string, sample: EventInviteeRow | null, entryUrl: string | null): string => {
  if (!sample) return text;
  const company = (sample.company_name ?? '').trim();
  const title = (sample.person_title ?? '').trim();
  const person = (sample.person_name ?? '').trim();
  const url = buildEntryUrl(entryUrl, sample.entry_token);
  const noPerson = person === '';
  return text
    .split('\n')
    .map((line) => {
      if (!PLACEHOLDER_RE.test(line)) return line;
      // 氏名なしの宛先は「{{氏名}} 様」を敬称ごと除去し、会社名を御中宛にする
      const source = noPerson ? line.replace(/\{\{氏名\}\}[ 　]*様?/g, '') : line;
      const replaced = source
        .split('{{会社名}}').join(noPerson && company !== '' ? `${company}　御中` : company)
        .split('{{役職}}').join(title)
        .split('{{氏名}}').join(person)
        .split('{{申込URL}}').join(url)
        .replace(/[ 　]{2,}/g, ' ')
        .replace(/^[ 　]+|[ 　]+$/g, '');
      return replaced === '' ? null : replaced;
    })
    .filter((line): line is string => line !== null)
    .join('\n');
};

type Props = {
  event: EventMaster;
  sampleInvitee: EventInviteeRow | null;
  canEdit: boolean;
  onSave: (patch: { mail_subject: string | null; mail_body: string | null }) => Promise<{ ok: boolean; error?: string }>;
};

export default function EventMailPanel({ event, sampleInvitee, canEdit, onSave }: Props) {
  const confirm = useConfirm();
  const notify = useNotification();

  const [subject, setSubject] = useState(event.mail_subject ?? '');
  const [body, setBody] = useState(event.mail_body ?? '');
  const [saving, setSaving] = useState(false);

  // イベント切替時 (および保存後のサーバー確定値反映時) に下書きを初期化する
  useEffect(() => {
    setSubject(event.mail_subject ?? '');
    setBody(event.mail_body ?? '');
  }, [event.id, event.mail_subject, event.mail_body]);

  const dirty = subject !== (event.mail_subject ?? '') || body !== (event.mail_body ?? '');

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const r = await onSave({ mail_subject: subject.trim() || null, mail_body: body.trim() ? body : null });
      if (r.ok) notify.success('案内メール文面を保存しました');
      else notify.error(r.error || '文面の保存に失敗しました');
    } finally {
      setSaving(false);
    }
  }, [onSave, subject, body, notify]);

  // --- 添付PDF (Firebase Storage) ---------------------------------------
  const prefix = `event-mail/${event.event_code}`;
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [attachLoading, setAttachLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadAttachments = useCallback(async () => {
    setAttachLoading(true);
    try {
      const res = await listAll(ref(storage, prefix));
      const items = await Promise.all(
        res.items.map(async (item) => {
          const meta = await getMetadata(item);
          return {
            name: item.name,
            fullPath: item.fullPath,
            size: meta.size ?? 0,
            updated: meta.updated ? new Date(meta.updated).toLocaleString('ja-JP') : null,
          };
        }),
      );
      setAttachments(items.sort((a, b) => a.name.localeCompare(b.name, 'ja')));
    } catch (e) {
      console.error('添付ファイル一覧の取得エラー:', e);
      notify.error('添付ファイル一覧の取得に失敗しました');
    } finally {
      setAttachLoading(false);
    }
  }, [prefix, notify]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const totalBytes = useMemo(() => attachments.reduce((s, a) => s + a.size, 0), [attachments]);

  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      if (!/\.pdf$/i.test(file.name)) {
        notify.warning('添付できるのは PDF ファイルのみです');
        return;
      }
      if (totalBytes + file.size > MAX_TOTAL_BYTES) {
        notify.error(`添付の合計が ${formatBytes(MAX_TOTAL_BYTES)} を超えるためアップロードできません`);
        return;
      }
      if (attachments.some((a) => a.name === file.name)) {
        const ok = await confirm({
          title: '上書きの確認',
          message: `同名のファイル「${file.name}」が既にあります。上書きしますか？`,
          confirmText: '上書きする',
        });
        if (!ok) return;
      }
      setUploading(true);
      try {
        await uploadBytes(ref(storage, `${prefix}/${file.name}`), file, { contentType: 'application/pdf' });
        notify.success(`「${file.name}」をアップロードしました`);
        await loadAttachments();
      } catch (err) {
        console.error('添付ファイルのアップロードエラー:', err);
        notify.error('アップロードに失敗しました');
      } finally {
        setUploading(false);
      }
    },
    [prefix, totalBytes, attachments, confirm, notify, loadAttachments],
  );

  const handleOpen = useCallback(
    async (item: AttachmentItem) => {
      try {
        const url = await getDownloadURL(ref(storage, item.fullPath));
        window.open(url, '_blank', 'noopener');
      } catch (e) {
        console.error('添付ファイルの取得エラー:', e);
        notify.error('ファイルを開けませんでした');
      }
    },
    [notify],
  );

  const handleDelete = useCallback(
    async (item: AttachmentItem) => {
      const ok = await confirm({
        title: '添付ファイルの削除',
        message: `「${item.name}」を削除しますか？`,
        confirmText: '削除する',
        destructive: true,
      });
      if (!ok) return;
      try {
        await deleteObject(ref(storage, item.fullPath));
        notify.success(`「${item.name}」を削除しました`);
        setAttachments((prev) => prev.filter((a) => a.fullPath !== item.fullPath));
      } catch (e) {
        console.error('添付ファイルの削除エラー:', e);
        notify.error('削除に失敗しました');
      }
    },
    [confirm, notify],
  );

  // --- プレビュー ---------------------------------------------------------
  const previewSubject = applyPlaceholders(subject, sampleInvitee, event.entry_url);
  const previewBody = applyPlaceholders(body, sampleInvitee, event.entry_url);

  const sectionTitleSx = { fontSize: '13px', fontWeight: 700, color: '#475569', display: 'flex', alignItems: 'center', gap: 0.75 };
  const cardSx = { borderRadius: '10px', boxShadow: '0 2px 4px -1px rgb(0 0 0 / 0.08)', border: '1px solid #f1f5f9' };

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pb: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: 2, alignItems: 'start' }}>
        {/* 文面編集 */}
        <Card sx={cardSx}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography sx={sectionTitleSx}>
                <Icons.EmailOutlined sx={{ fontSize: '18px' }} /> 案内メール文面
              </Typography>
              {canEdit && (
                <Button
                  onClick={handleSave}
                  disabled={!dirty || saving}
                  startIcon={saving ? <CircularProgress size={13} color="inherit" /> : <Icons.Save />}
                  sx={STANDARD_CTA_BUTTON_COMPACT_SX}
                >
                  文面を保存する
                </Button>
              )}
            </Box>
            <TextField
              label="件名"
              size="small"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={!canEdit}
              slotProps={{ htmlInput: { maxLength: 255 } }}
            />
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>差し込み</Typography>
              {(['{{会社名}}', '{{役職}}', '{{氏名}}', '{{申込URL}}'] as const).map((ph) => (
                <Tooltip
                  key={ph}
                  title={
                    ph === '{{申込URL}}'
                      ? '配信時に宛先ごとの申込特定トークン付きURLへ置換されます (申込者の自動特定に使用)'
                      : '本文・件名に挿入すると配信時に宛先ごとの実名へ置換されます'
                  }
                  arrow
                >
                  <Chip
                    size="small"
                    label={ph}
                    clickable={canEdit}
                    onClick={canEdit ? () => setBody((prev) => `${prev}${ph}`) : undefined}
                    sx={{ fontSize: '11px', height: '22px', fontWeight: 700, fontFamily: 'monospace', bgcolor: '#eff6ff', color: '#3b82f6' }}
                  />
                </Tooltip>
              ))}
              <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>
                例）{'{{会社名}}'} {'{{役職}}'} {'{{氏名}}'} 様 → 宛名行を自動生成（役職なしは詰め、氏名なしは「会社名　御中」）
              </Typography>
            </Box>
            <TextField
              label="本文"
              size="small"
              fullWidth
              multiline
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={!canEdit}
              sx={{ '& textarea': { fontSize: '13px', lineHeight: 1.7, resize: 'vertical' } }}
            />
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* 添付PDF */}
          <Card sx={cardSx}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={sectionTitleSx}>
                  <Icons.AttachFile sx={{ fontSize: '18px' }} /> 添付PDF
                  <Typography component="span" sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>
                    合計 {formatBytes(totalBytes)} / {formatBytes(MAX_TOTAL_BYTES)}
                  </Typography>
                </Typography>
                {canEdit && (
                  <>
                    <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" hidden onChange={handleUpload} />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      startIcon={uploading ? <CircularProgress size={13} color="inherit" /> : <Icons.CloudUpload />}
                      sx={STANDARD_CTA_BUTTON_COMPACT_SX}
                    >
                      PDFを追加
                    </Button>
                  </>
                )}
              </Box>
              {attachLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : attachments.length === 0 ? (
                <Typography sx={{ fontSize: '12px', color: '#94a3b8', py: 1 }}>
                  添付ファイルはありません。配信メールに添付する PDF をアップロードしてください。
                </Typography>
              ) : (
                attachments.map((a) => (
                  <Box
                    key={a.fullPath}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, borderRadius: '8px', border: '1px solid #f1f5f9', bgcolor: '#f8fafc' }}
                  >
                    <Icons.PictureAsPdf sx={{ fontSize: '18px', color: '#dc2626' }} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.name}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>
                        {formatBytes(a.size)}
                        {a.updated ? ` ・ ${a.updated}` : ''}
                      </Typography>
                    </Box>
                    <Tooltip title="内容を確認 (別タブで開く)" arrow>
                      <IconButton size="small" onClick={() => handleOpen(a)}>
                        <Icons.OpenInNew sx={{ fontSize: '16px', color: '#64748b' }} />
                      </IconButton>
                    </Tooltip>
                    {canEdit && (
                      <Tooltip title="削除" arrow>
                        <IconButton size="small" onClick={() => handleDelete(a)}>
                          <Icons.Delete sx={{ fontSize: '16px', color: '#94a3b8' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ))
              )}
            </CardContent>
          </Card>

          {/* 差し込みプレビュー */}
          <Card sx={cardSx}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={sectionTitleSx}>
                <Icons.Visibility sx={{ fontSize: '18px' }} /> 差し込みプレビュー
                {sampleInvitee && (
                  <Typography component="span" sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>
                    宛先例）{sampleInvitee.company_name ?? ''} {sampleInvitee.person_name ?? ''} 様
                  </Typography>
                )}
              </Typography>
              {subject.trim() === '' && body.trim() === '' ? (
                <Typography sx={{ fontSize: '12px', color: '#94a3b8', py: 1 }}>
                  文面を入力するとここに差し込み後のプレビューが表示されます。
                </Typography>
              ) : (
                <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: 1.5, bgcolor: '#fff' }}>
                  {previewSubject && (
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', pb: 1, mb: 1, borderBottom: '1px solid #f1f5f9' }}>
                      {previewSubject}
                    </Typography>
                  )}
                  <Typography component="pre" sx={{ fontSize: '12.5px', color: '#334155', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, fontFamily: 'inherit', lineHeight: 1.7 }}>
                    {previewBody}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
